/**
 * Auth API service.
 *
 * All auth-related HTTP calls to /auth/* endpoints.
 * Uses the shared apiClient for headers, error handling, and request IDs.
 *
 * @see contracts/api-contracts.yaml -- /auth/* endpoints
 */

import { apiClient } from './api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  avatar_url: string | null;
  role: 'admin' | 'maintainer' | 'member';
  status: 'active' | 'invited' | 'disabled' | 'locked';
  locale: string;
  timezone: string;
  totp_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * GET /api/v1/me (contract UserSelf): the signed-in user plus the flags the store does not keep.
 * is_platform_owner is false under impersonation; absent means "not an owner" (fail closed).
 */
export interface UserSelf extends AuthUser {
  is_platform_owner?: boolean;
  // --- FEAT-052 TASK-028 ---
  /**
   * IANA zone the server uses to judge report/cost/analytics dates (validated, any role). Optional:
   * an older API without the field makes useOrgTimeZone fall back. Kept OUT of AuthUser/the store.
   */
  org_timezone?: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: AuthUser;
}

export interface TwoFactorRequired {
  requires_2fa: true;
  temp_token: string;
}

export interface RegisterByInvitationRequest {
  token: string;
  display_name: string;
  password: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

export interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

// ---------------------------------------------------------------------------
// Type guard
// ---------------------------------------------------------------------------

export function isTwoFactorRequired(
  response: LoginResponse | TwoFactorRequired,
): response is TwoFactorRequired {
  return 'requires_2fa' in response && response.requires_2fa === true;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const authService = {
  /**
   * POST /auth/login
   * Returns 200 (tokens + user) or 202 (requires 2FA).
   */
  async login(
    email: string,
    password: string,
  ): Promise<LoginResponse | TwoFactorRequired> {
    return apiClient.post<LoginResponse | TwoFactorRequired>(
      '/v1/auth/login',
      { email, password },
    );
  },

  /**
   * POST /auth/2fa/verify
   * Verify TOTP code with temp_token from login 202.
   */
  async verify2fa(
    temp_token: string,
    code: string,
  ): Promise<LoginResponse> {
    return apiClient.post<LoginResponse>('/v1/auth/2fa/verify', {
      temp_token,
      code,
    });
  },

  /**
   * POST /auth/register-by-invitation
   * Create user account via invitation token.
   */
  async registerByInvitation(
    data: RegisterByInvitationRequest,
  ): Promise<AuthUser> {
    return apiClient.post<AuthUser>('/v1/auth/register-by-invitation', data);
  },

  /**
   * POST /auth/forgot-password
   * Always returns 202 (prevents email enumeration).
   */
  async forgotPassword(
    email: string,
  ): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(
      '/v1/auth/forgot-password',
      { email },
    );
  },

  /**
   * POST /auth/reset-password
   * Set new password using reset token.
   */
  async resetPassword(
    data: ResetPasswordRequest,
  ): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(
      '/v1/auth/reset-password',
      data,
    );
  },

  /**
   * POST /auth/refresh
   * Rotate refresh token and get new access + refresh pair.
   */
  async refresh(
    refresh_token: string,
  ): Promise<RefreshResponse> {
    return apiClient.post<RefreshResponse>('/v1/auth/refresh', {
      refresh_token,
    });
  },

  /**
   * GET /me
   * Fresh profile of the signed-in user (locale and timezone included). Used to
   * complete sessions opened before login returned those fields.
   */
  async getMe(): Promise<UserSelf> {
    return apiClient.get<UserSelf>('/v1/me');
  },

  /**
   * POST /auth/logout
   * Revoke current session.
   */
  async logout(all_sessions = false): Promise<void> {
    return apiClient.post<void>('/v1/auth/logout', { all_sessions });
  },
};
